import React from 'react';
import cn from './cn';

/**
 * shadcn's Card, sized and coloured like the CMS `.module` panel.
 *
 * What changes visually: `.module` was a bare white rectangle with no edge at all, so on
 * the grey page background the panels ran together. This gives it a hairline border, the
 * 5px radius the modals already use, and a shadow slight enough to read as a surface
 * rather than a popup. That is the whole of the "slightly optimized" in this component.
 *
 * The header keeps its bottom rule, because the CMS uses the card title as a section
 * heading rather than a caption and the rule is what separates it from the table beneath.
 *
 * **`tw-border-0` before every directional border, and it is load-bearing.** Tailwind runs
 * here with `preflight: false`, because its reset would pull base.css and main.css out from
 * under every screen. Preflight is also what normally sets
 * `border-width: 0` on every element, so without it an element's unset sides keep the CSS
 * default of `medium`, which is 3px. `tw-border-solid` sets the *style* on all four sides
 * and `tw-border-b` sets only the bottom *width*, so the other three drew at 3px: a thick
 * bracket around every card header and footer, just inside the card's own 1px edge. That
 * is the "double outline". Zeroing all four first and then setting the one side fixes it,
 * and tailwind-merge keeps both because `border-0` and `border-b` are different groups.
 */
export const CardRoot = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'tw-bg-white tw-rounded-card tw-border tw-border-solid tw-border-line-light',
      'tw-shadow-sm',
      className
    )}
    {...props}
  />
));
CardRoot.displayName = 'CardRoot';

export const CardHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'tw-flex tw-items-center tw-justify-between tw-gap-3',
      'tw-px-4 tw-py-3 tw-border-0 tw-border-b tw-border-solid tw-border-line-light',
      className
    )}
    {...props}
  />
));
CardHeader.displayName = 'CardHeader';

export const CardTitle = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn('tw-text-section tw-font-bold tw-text-ink tw-m-0', className)}
    {...props}
  />
));
CardTitle.displayName = 'CardTitle';

export const CardContent = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('tw-px-4 tw-py-4', className)} {...props} />
));
CardContent.displayName = 'CardContent';

export const CardFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'tw-flex tw-items-center tw-justify-end tw-gap-2',
      'tw-px-4 tw-py-3 tw-border-0 tw-border-t tw-border-solid tw-border-line-light',
      className
    )}
    {...props}
  />
));
CardFooter.displayName = 'CardFooter';

export default CardRoot;
